"use client"

import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { Card, CardContent } from '@/components/ui/card'
import { Loader2, Send, Sparkles } from "lucide-react"
import BartenderResponse from '@/components/bartender-response'

export default function AskBartenderPage() {
  const [prompt, setPrompt] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [response, setResponse] = useState(null)

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!prompt.trim()) return

    setIsLoading(true)

    try {
      // This will be implemented with the OpenAI API
      const result = await fetch("/api/bartender", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ prompt }),
      })

      const data = await result.json()
      setResponse(data)
    } catch (error) {
      console.error("Error asking bartender:", error)
    } finally {
      setIsLoading(false)
    }
  }

  const examplePrompts = [
    "What cocktail can I make with vodka and orange juice?",
    "Suggest a non-alcoholic drink for a summer party",
    "What's a good cocktail for a date night?",
    "I have rum, lime, and mint. What can I make?",
  ]

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <div className="text-center mb-8 animate-fade-in">
        <h1 className="text-4xl font-bold mb-4">Ask the Bartender</h1>
        <p className="text-xl text-muted-foreground">Get personalized drink recommendations from our AI bartender</p>
      </div>

      <Card className="mb-8 animate-slide-up">
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <Textarea
              placeholder="Describe what you're looking for... (e.g., 'I want a refreshing summer drink with rum' or 'What's a good non-alcoholic option for a party?')"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              className="min-h-32 resize-none"
            />

            <div className="flex justify-end">
              <Button type="submit" disabled={isLoading || !prompt.trim()}>
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Thinking...
                  </>
                ) : (
                  <>
                    <Send className="mr-2 h-4 w-4" />
                    Ask Bartender
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {!response && (
        <div className="space-y-6 animate-fade-in">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            <h2 className="text-xl font-semibold">Try asking about:</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {examplePrompts.map((prompt, index) => (
              <Button
                key={index}
                variant="outline"
                className="justify-start h-auto py-3 px-4 text-left"
                onClick={() => setPrompt(prompt)}
              >
                {prompt}
              </Button>
            ))}
          </div>
        </div>
      )}

      {response && <BartenderResponse response={response} className="animate-fade-in" />}
    </div>
  )
}
